<template>
    <LoadingComponent :props="loading" />

    <div id="ai-provider-settings" class="db-tab-div active">
        <div class="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-5 bg-white border rounded-md p-3">
            <button
                v-for="(provider, index) in providerTabs"
                :key="provider"
                type="button"
                class="db-tab-sub-btn w-full flex items-center gap-3 h-10 px-4 rounded-lg transition bg-white hover:text-primary hover:bg-primary/5"
                :class="index === selectIndex ? 'active' : ''"
                @click.stop.prevent="selectActive(index)"
            >
                <span class="capitalize whitespace-nowrap text-[15px]">
                    {{ tabLabel(provider) }}
                </span>
            </button>
        </div>

        <div
            v-if="activeProvider"
            :key="activeProvider"
            :id="activeProvider"
            class="db-card db-tab-sub-div active"
            :class="{ 'on-update': onUpdateActive }"
        >
            <div class="db-card-header items-center justify-between gap-4">
                <h3 class="db-card-title">{{ tabLabel(activeProvider) }}</h3>
                <div class="flex items-center gap-2" v-if="activeProviderData">
                    <div class="flex items-center gap-2 pr-2 border-r border-gray-200">
                        <span class="text-sm">{{ $t('label.enable') }}</span>
                        <div class="custom-switch">
                            <input
                                type="checkbox"
                                :checked="!!activeProviderData.is_enabled"
                                @change="onProviderEnabledToggle(activeProvider, $event.target.checked)"
                            >
                        </div>
                    </div>
                    <div class="flex items-center gap-2">
                        <span class="text-sm">{{ $t('label.default') }}</span>
                        <div class="custom-switch">
                            <input
                                type="checkbox"
                                :checked="!!activeProviderData.is_default"
                                @change="onProviderDefaultToggle(activeProvider, $event.target.checked)"
                            >
                        </div>
                    </div>
                    <button
                        type="button"
                        class="db-btn h-9 py-2 px-3 text-white bg-primary ml-3"
                        @click="refreshFromServer"
                        :disabled="isRefreshingView"
                        :class="{ 'disabled:opacity-50 disabled:bg-gray': isRefreshingView }"
                    >
                        <template v-if="isRefreshingView">
                            <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                            </svg>
                        </template>
                        <template v-else>
                            <i class="fa-solid fa-arrows-rotate"></i>
                        </template>
                    </button>
                </div>
            </div>
            <div class="db-card-body" v-if="activeProviderData">
            <form @submit.prevent="saveProviderSettings" class="w-full d-block" autocomplete="off" data-lpignore="true">
                <div class="form-row">
                    <input type="text" class="hidden" tabindex="-1" autocomplete="username" aria-hidden="true" />
                    <input type="password" class="hidden" tabindex="-1" autocomplete="new-password" aria-hidden="true" />

                    <div class="form-col-12">
                        <label class="db-field-title">{{ $t('label.available_sdk_models') }}</label>
                        <div class="grid grid-cols-1 gap-2 border border-gray-200 rounded-md p-3">
                            <label
                                v-for="modelRow in activeProviderData.models"
                                :key="activeProvider + '-' + modelRow.model"
                                class="flex items-center gap-2 text-sm"
                            >
                                <input
                                    type="checkbox"
                                    v-model="modelRow.is_enabled"
                                    @change="onModelToggle(activeProvider, modelRow.model)"
                                />
                                <span>{{ modelRow.model }}</span>
                            </label>
                        </div>
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">{{ $t('label.default_model_selector') }}</label>
                        <vue-select
                            :key="defaultModelSelectKey"
                            class="db-field-control f-b-custom-select"
                            v-model="activeProviderData.model"
                            :options="defaultModelOptions(activeProvider)"
                            label-by="name"
                            value-by="id"
                            :closeOnSelect="true"
                            :searchable="true"
                            :clearOnClose="true"
                            @update:modelValue="onDefaultModelChange(activeProvider, $event)"
                        />
                        <small class="db-field-alert" v-if="errors[activeProvider + '.model']">{{ errors[activeProvider + '.model'] }}</small>
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title flex items-center gap-2">
                            <span class="w-fit nowrap">
                                {{ $t('label.api_key') }}
                            </span>
                            <!-- <span class="w-auto">
                                <small class="db-field-alert whitespace-nowrap overflow-hidden text-ellipsis truncate" v-if="activeProviderData.api_key_masked">
                                    {{ $t('label.current_key') }}: {{ activeProviderData.api_key_masked }}
                                </small>
                            </span> -->
                        </label>
                        <input
                            v-model="activeProviderData.api_key"
                            type="password"
                            class="db-field-control"
                            :name="`api_key_${activeProvider}`"
                            autocomplete="new-password"
                            autocorrect="off"
                            autocapitalize="none"
                            spellcheck="false"
                        />
                    </div>

                    <div
                        v-for="field in selectedModelParameterSchema"
                        :key="activeProvider + '-' + selectedModelKey + '-' + field.key"
                        class="form-col-12 sm:form-col-6"
                    >
                        <label class="db-field-title">{{ fieldLabel(field) }}</label>
                        <input
                            v-model.number="selectedModelParameters[field.key]"
                            :type="fieldInputType(field)"
                            :min="field.min"
                            :max="field.max"
                            :step="field.step || 1"
                            class="db-field-control"
                        />
                    </div>

                    <div class="form-col-12 border-t pt-5 mt-3">
                        <button type="submit" class="db-btn text-white bg-primary" :disabled="isSaving">
                            <template v-if="isSaving">
                                <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('button.updating') }}</span>
                            </template>
                            <template v-else>
                                <i class="fa-solid fa-floppy-disk"></i>
                                <span>{{ $t('button.save_settings') }}</span>
                            </template>
                        </button>
                    </div>
                </div>
            </form>
            </div>
        </div>
    </div>
</template>

<script>
import LoadingComponent from '../../components/LoadingComponent';
import alertService from '../../../../services/alertService';

export default {
    name: 'AiProviderSettingComponent',
    components: { LoadingComponent },
    data() {
        const providerTabs = ['openai', 'google', 'anthropic'];
        const providerFormByKey = providerTabs.reduce((carry, provider) => {
            carry[provider] = {
                provider,
                is_enabled: false,
                is_default: false,
                model: null,
                api_key: '',
                api_key_masked: null,
                parameters: {
                    temperature: 0.2,
                    max_tokens: 4096,
                },
                models: [],
            };

            return carry;
        }, {});

        return {
            loading: {
                isActive: false,
            },
            selectIndex: 0,
            providerTabs,
            providerFormByKey,
            modelSelectRenderNonce: 0,
            isSaving: false,
            isRefreshingView: false,
            onUpdateActive: false,
            errors: {},
        };
    },
    mounted() {
        this.fetchProviderSettings();
    },
    computed: {
        activeProvider() {
            return this.providerTabs[this.selectIndex] || this.providerTabs[0] || null;
        },
        activeProviderData() {
            if (!this.activeProvider) {
                return null;
            }

            return this.providerFormByKey[this.activeProvider] || null;
        },
        defaultModelSelectKey() {
            const provider = this.activeProvider || 'none';
            const providerData = this.activeProviderData;
            const selectedModel = providerData?.model || 'none';
            const totalModels = providerData?.models?.length || 0;
            const enabledModels = providerData?.models?.filter((item) => !!item.is_enabled).length || 0;

            return `${provider}-${selectedModel}-${totalModels}-${enabledModels}-${this.modelSelectRenderNonce}`;
        },
        selectedModelRow() {
            if (!this.activeProviderData) {
                return null;
            }

            const selectedModel = this.normalizeScalarSelectValue(this.activeProviderData.model);
            return this.activeProviderData.models.find((item) => item.model === selectedModel) || null;
        },
        selectedModelParameterSchema() {
            return this.selectedModelRow?.parameter_schema || [];
        },
        selectedModelKey() {
            return this.selectedModelRow?.model || 'none';
        },
        selectedModelParameters() {
            if (!this.selectedModelRow) {
                return {};
            }

            if (!this.selectedModelRow.parameters || typeof this.selectedModelRow.parameters !== 'object') {
                this.selectedModelRow.parameters = {};
            }

            return this.selectedModelRow.parameters;
        },
    },
    methods: {
        fieldLabel(field) {
            const labelKey = field?.label;
            if (labelKey && typeof labelKey === 'string') {
                return this.$t(labelKey);
            }

            return field?.key || '';
        },
        fieldInputType(field) {
            const fieldType = field?.type;
            return fieldType === 'number' || fieldType === 'integer' ? 'number' : 'text';
        },
        normalizeParametersBySchema(parameters, schema) {
            const source = parameters && typeof parameters === 'object' ? parameters : {};
            const result = {};

            (schema || []).forEach((field) => {
                const key = field?.key;
                if (!key) {
                    return;
                }

                const hasValue = Object.prototype.hasOwnProperty.call(source, key);
                const rawValue = hasValue ? source[key] : field?.default;
                if (rawValue === null || rawValue === undefined || rawValue === '') {
                    return;
                }

                if (field?.type === 'integer') {
                    const value = parseInt(rawValue, 10);
                    if (Number.isFinite(value)) {
                        result[key] = value;
                    }
                    return;
                }

                if (field?.type === 'number') {
                    const value = Number(rawValue);
                    if (Number.isFinite(value)) {
                        result[key] = value;
                    }
                    return;
                }

                result[key] = rawValue;
            });

            return result;
        },
        normalizeScalarSelectValue(value) {
            if (value && typeof value === 'object') {
                return value.id ?? value.value ?? null;
            }

            return value ?? null;
        },
        selectActive(index) {
            if (index < 0 || index >= this.providerTabs.length) {
                return;
            }

            this.selectIndex = index;
        },
        tabLabel(provider) {
            if (provider === 'openai') {
                return this.$t('label.ai_provider_openai');
            }
            if (provider === 'google') {
                return this.$t('label.ai_provider_google');
            }
            return this.$t('label.ai_provider_anthropic');
        },
        defaultModelOptions(provider) {
            const providerData = this.providerFormByKey[provider];
            if (!providerData) {
                return [];
            }

            const selectedModel = this.normalizeScalarSelectValue(providerData.model);

            return providerData.models
                .filter((item) => !!item.is_enabled || item.model === selectedModel)
                .map((item) => ({
                    id: item.model,
                    name: item.model,
                }));
        },
        onModelToggle(provider, model) {
            const providerData = this.providerFormByKey[provider];
            if (!providerData) {
                return;
            }

            const row = providerData.models.find((item) => item.model === model);
            if (!row) {
                return;
            }

            if (!row.is_enabled && providerData.model === model) {
                const firstEnabled = providerData.models.find((item) => item.is_enabled);
                providerData.model = firstEnabled ? firstEnabled.model : null;
            }
        },
        onDefaultModelChange(provider, model) {
            const providerData = this.providerFormByKey[provider];
            const normalizedModel = this.normalizeScalarSelectValue(model);

            if (!providerData || !normalizedModel) {
                return;
            }

            providerData.model = normalizedModel;

            const row = providerData.models.find((item) => item.model === normalizedModel);
            if (row) {
                row.is_enabled = true;
            }
        },
        onProviderEnabledToggle(provider, isEnabled) {
            const providerData = this.providerFormByKey[provider];
            if (!providerData) {
                return;
            }

            providerData.is_enabled = !!isEnabled;

            if (!providerData.is_enabled && providerData.is_default) {
                providerData.is_default = false;
                const fallback = this.providerTabs.find((item) => item !== provider && this.providerFormByKey[item]?.is_enabled);
                if (fallback) {
                    this.providerFormByKey[fallback].is_default = true;
                }
            }
        },
        onProviderDefaultToggle(provider, isDefault) {
            const providerData = this.providerFormByKey[provider];
            if (!providerData) {
                return;
            }

            if (!isDefault) {
                providerData.is_default = false;
                return;
            }

            this.providerTabs.forEach((item) => {
                this.providerFormByKey[item].is_default = item === provider;
            });

            providerData.is_enabled = true;
        },
        hydrateProviderMap(rows) {
            const providerMap = {};

            this.providerTabs.forEach((provider) => {
                providerMap[provider] = {
                    provider,
                    is_enabled: false,
                    is_default: false,
                    model: null,
                    api_key: '',
                    api_key_masked: null,
                    parameters: {
                        temperature: 0.2,
                        max_tokens: 4096,
                    },
                    models: [],
                };
            });

            (rows || []).forEach((providerRow) => {
                const providerName = providerRow.provider;
                if (!providerMap[providerName]) {
                    return;
                }

                providerMap[providerName].is_enabled = !!providerRow.is_enabled;
                providerMap[providerName].is_default = !!providerRow.is_default;
                providerMap[providerName].api_key = providerRow.api_key || '';
                providerMap[providerName].api_key_masked = providerRow.api_key_masked || null;

                providerMap[providerName].models = (providerRow.models || []).map((item) => ({
                    model: item.model || '',
                    is_enabled: !!item.is_enabled || !!item.is_default,
                    is_default: !!item.is_default,
                    parameter_schema: item?.parameter_schema || [],
                    parameters: {
                        ...this.normalizeParametersBySchema(item?.parameters, item?.parameter_schema),
                    },
                }));

                const defaultModel = providerMap[providerName].models.find((item) => item.is_default);
                const firstEnabled = providerMap[providerName].models.find((item) => item.is_enabled);
                providerMap[providerName].model = defaultModel?.model || firstEnabled?.model || null;

                const selectedModelRow = providerMap[providerName].models.find((item) => item.model === providerMap[providerName].model);
                if (selectedModelRow) {
                    providerMap[providerName].parameters = {
                        ...this.normalizeParametersBySchema(selectedModelRow?.parameters, selectedModelRow?.parameter_schema),
                    };
                }
            });

            const hasDefaultProvider = Object.values(providerMap).some((item) => item.is_default);
            if (!hasDefaultProvider) {
                const fallback = this.providerTabs.find((provider) => providerMap[provider]?.is_enabled) || this.providerTabs[0];
                if (fallback && providerMap[fallback]) {
                    providerMap[fallback].is_default = true;
                    providerMap[fallback].is_enabled = true;
                }
            }

            return providerMap;
        },
        async fetchProviderSettings({ silent = false } = {}) {
            if (!silent) {
                this.loading.isActive = true;
            }
            try {
                const providerRes = await this.$store.dispatch('aiProviderSetting/providerSettings');

                const providerMap = this.hydrateProviderMap(providerRes.data.data || []);

                this.providerFormByKey = providerMap;
                this.modelSelectRenderNonce += 1;
                this.errors = {};
            } catch (err) {
                alertService.error(err.response?.data?.message || err.message || err);
            } finally {
                this.loading.isActive = false;
            }
        },
        async refreshFromServer() {
            if (this.isRefreshingView) {
                return;
            }
            this.isRefreshingView = true;
            this.onUpdateActive = true;
            try {
                await this.fetchProviderSettings({ silent: true });
                alertService.info(this.$t('button.refresh'));
            } finally {
                this.isRefreshingView = false;
                this.onUpdateActive = false;
            }
        },
        async saveProviderSettings() {
            this.isSaving = true;
            this.onUpdateActive = true;
            this.loading.isActive = false;
            try {
                const providerPayload = [];

                this.providerTabs.forEach((provider) => {
                    const providerData = this.providerFormByKey[provider];
                    if (!providerData) {
                        return;
                    }

                    const selectedModel = this.normalizeScalarSelectValue(providerData.model);

                    const modelsPayload = providerData.models.map((modelItem) => {
                        const isDefault = selectedModel === modelItem.model;
                        const isEnabled = !!modelItem.is_enabled || isDefault;
                        const normalizedParameters = this.normalizeParametersBySchema(modelItem?.parameters, modelItem?.parameter_schema);

                        return {
                            model: modelItem.model,
                            is_enabled: isEnabled,
                            is_default: isDefault,
                            parameters: normalizedParameters,
                        };
                    });

                    providerPayload.push({
                        provider: providerData.provider,
                        is_enabled: !!providerData.is_enabled || !!providerData.is_default,
                        is_default: !!providerData.is_default,
                        api_key: providerData.api_key || '',
                        models: modelsPayload,
                    });
                });

                const res = await this.$store.dispatch('aiProviderSetting/saveProviderSettings', {
                    providers: providerPayload,
                });
                const providerMap = this.hydrateProviderMap(res.data.data || []);

                this.providerFormByKey = providerMap;
                this.modelSelectRenderNonce += 1;
                this.errors = {};
                const savedActive = (res?.data?.data || []).find((item) => item.provider === this.activeProvider);
                const savedModel = savedActive?.models?.find((item) => !!item.is_default)?.model || savedActive?.models?.find((item) => !!item.is_enabled)?.model || '--';
                alertService.success(`${this.$t('message.updated_settings')} (${this.tabLabel(this.activeProvider)}: ${savedModel})`);
                await this.fetchProviderSettings({ silent: true });
            } catch (err) {
                this.errors = err.response?.data?.errors || {};
                const firstValidationError = Object.values(this.errors).flat().find((message) => !!message);
                alertService.error(firstValidationError || err.response?.data?.message || err.message || err);
            } finally {
                this.loading.isActive = false;
                this.isSaving = false;
                this.onUpdateActive = false;
            }
        },
    },
};
</script>
